import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';
import { parseCSVObjects } from '../lib/csv';

const A = (o, p) => ({ line1: o[`${p} Address`] || '', line2: o[`${p} Street2`] || '', city: o[`${p} City`] || '', state: o[`${p} State`] || '', postal_code: o[`${p} Code`] || '' });
const hasA = (a) => !!(a.line1 || a.city || a.state || a.postal_code);
const sameA = (a, b) => ['line1', 'line2', 'city', 'state', 'postal_code'].every((k) => (a[k] || '') === (b[k] || ''));

function mapZohoCustomer(o) {
  const name = (o['Display Name'] || `${o['First Name'] || ''} ${o['Last Name'] || ''}`.trim() || o['Company Name'] || '').trim();
  const billing = A(o, 'Billing');
  const shipping = A(o, 'Shipping');
  const shipping_same = !hasA(shipping) || sameA(billing, shipping);
  const cn = `${o['First Name'] || ''} ${o['Last Name'] || ''}`.trim();
  return {
    name,
    company: o['Company Name'] || '',
    email: o['EmailID'] || '',
    phone: o['Phone'] || o['MobilePhone'] || '',
    billing_address: hasA(billing) ? billing : null,
    shipping_address: (!shipping_same && hasA(shipping)) ? shipping : null,
    shipping_same,
    is_active: (o['Status'] || '').toLowerCase() !== 'inactive',
    contact: cn ? { name: cn, title: o['Designation'] || '', email: o['EmailID'] || '', phone: o['MobilePhone'] || o['Phone'] || '' } : null,
  };
}

export default function Import() {
  const { activeId, activeBusiness } = useBusiness();
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function onFile(file) {
    setError(null); setResult(null); setRows(null); setFileName(file ? file.name : '');
    if (!file) return;
    try {
      const objs = parseCSVObjects(await file.text());
      if (!objs.length) { setError('No rows found in that file.'); return; }
      if (!('Display Name' in objs[0]) && !('Company Name' in objs[0])) {
        setError('This doesn’t look like a Zoho contacts export (no “Display Name” column).'); return;
      }
      const mapped = objs.map(mapZohoCustomer).filter((m) => m.name);
      if (!mapped.length) { setError('No customers with a usable name were found.'); return; }
      setRows(mapped);
    } catch { setError('Could not read that file.'); }
  }

  async function runImport() {
    if (!rows || !rows.length) return;
    setBusy(true); setError(null); setResult(null);
    try {
      setResult(await api('/import/customers', { method: 'POST', body: { business_id: activeId, rows } }));
    } catch (e) { setError(e?.detail || 'Import failed.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Import" />
      <section className="panel" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Import customers from Zoho Books</h3>
        <p className="hint">
          Upload the <strong>Contacts</strong> CSV exported from Zoho Books. New customers are added to{' '}
          <strong>{activeBusiness?.name || 'the selected business'}</strong>. Existing customers (matched by email, or by name) are skipped, so it’s safe to re-run.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files && e.target.files[0])} />
        {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}

        {rows && (
          <>
            <p style={{ marginTop: 16 }}><strong>{rows.length}</strong> customers ready{fileName ? ` from ${fileName}` : ''}.</p>
            <div className="export-scroll">
              <table className="table">
                <thead><tr><th>Name</th><th>Company</th><th>Email</th><th>City</th></tr></thead>
                <tbody>
                  {rows.slice(0, 8).map((r, i) => (
                    <tr key={i}><td>{r.name}</td><td>{r.company || '—'}</td><td>{r.email || '—'}</td><td>{r.billing_address?.city || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 8 && <p className="hint">…and {rows.length - 8} more.</p>}
            <button className="btn btn--primary" style={{ marginTop: 12 }} disabled={busy} onClick={runImport}>
              {busy ? 'Importing…' : `Import ${rows.length} customers`}
            </button>
          </>
        )}

        {result && (
          <div style={{ marginTop: 16 }}>
            <p><strong>{result.created}</strong> imported · <strong>{result.skipped_count}</strong> skipped (already exist){result.errors?.length ? ` · ${result.errors.length} errors` : ''}.</p>
            {result.skipped?.length > 0 && <details><summary>{result.skipped_count} skipped</summary><p className="hint">{result.skipped.join(', ')}</p></details>}
            {result.errors?.length > 0 && <details><summary>{result.errors.length} errors</summary>{result.errors.map((e, i) => <p key={i} className="hint">{e.name}: {e.error}</p>)}</details>}
          </div>
        )}
      </section>
    </>
  );
}
